import { useState } from "react";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import { IconStack, IconPlus } from "@tabler/icons-react";
import { ElementRow } from "./ElementRow";
import { CompoundElementRow } from "./CompoundElementRow";
import { AddItemDialog } from "./AddItemDialog";
import type { ElementWithPendingEdit } from "@/api/common";

interface CompoundItem {
  elements: ElementWithPendingEdit[];
}

interface ElementGroup {
  id: string;
  name: string;
  description?: string | null;
  itemCount: number;
  fieldsPerItem?: number | null;
  templateCode?: string;
  placeholders?: Array<{
    name: string;
    description: string;
    type: string;
  }>;
  compoundItems: CompoundItem[];
}

interface ElementGroupRowProps {
  group: ElementGroup;
  sectionId: string;
  sectionName: string;
  projectId: string;
  onAddItem?: (groupId: string, values: Record<string, string>) => void;
}

export function ElementGroupRow({
  group,
  sectionId,
  sectionName,
  projectId,
  onAddItem,
}: ElementGroupRowProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const hasTemplate = !!group.templateCode;

  // Get element type from first element (for Add dialog)
  const firstElement = group.compoundItems[0]?.elements[0];
  const elementType = firstElement?.type;

  // Determine if this is a compound group
  const fieldsPerItem = group.fieldsPerItem || 1;
  const isCompoundGroup = fieldsPerItem > 1;

  // Count of items (from API)
  const itemCount = group.compoundItems.length;

  const handleAddItem = (values: Record<string, string>) => {
    onAddItem?.(group.id, values);
    setShowAddDialog(false);
  };

  return (
    <div className="border-l-2 border-dashed border-primary/30 ml-4 pl-4 space-y-2">
      {/* Group Header */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <IconStack className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">{group.name}</span>
          <Badge variant="outline" className="text-[10px]">
            {itemCount} item{itemCount !== 1 ? "s" : ""}
          </Badge>
          {isCompoundGroup && (
            <Badge variant="secondary" className="text-[10px]">
              {fieldsPerItem} fields each
            </Badge>
          )}
        </div>

        {hasTemplate && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setShowAddDialog(true)}
          >
            <IconPlus className="w-3 h-3 mr-1" />
            Add Item
          </Button>
        )}
      </div>

      {/* Group Description */}
      {group.description && (
        <p className="text-xs text-muted-foreground -mt-1 mb-2">
          {group.description}
        </p>
      )}

      {/* Render pre-grouped compound items from API */}
      <div className="space-y-2">
        {group.compoundItems.map((item, idx) => {
          // Items with multiple elements or fieldsPerItem > 1 are compound
          const isCompoundItem = item.elements.length > 1;

          if (isCompoundItem) {
            return (
              <CompoundElementRow
                key={`compound-${idx}-${item.elements[0]?.id}`}
                elements={item.elements}
                sectionId={sectionId}
                sectionName={sectionName}
                projectId={projectId}
              />
            );
          } else {
            // Single element item
            const element = item.elements[0];
            if (!element) return null;
            return (
              <ElementRow
                key={element.id}
                element={element}
                sectionId={sectionId}
                sectionName={sectionName}
                projectId={projectId}
              />
            );
          }
        })}
      </div>

      {/* Add Item Dialog */}
      {hasTemplate && (
        <AddItemDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          groupName={group.name}
          placeholders={group.placeholders || []}
          elementType={elementType}
          onAdd={handleAddItem}
        />
      )}
    </div>
  );
}
